import React, { useMemo } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import _isEqual from 'lodash/isEqual';
import { TranslateFunction } from 'react-localize-redux';

import { PriceTag, Snackbar, Typography } from '../../../components/atoms';
import { Currency } from '../../../core/api/general/general.types';
import { OrderHistoryItems } from '../../../core/api/order/order.types';
import { formatNumberToCurrency } from '../../../utils/basketUtils';

const useStyles = makeStyles((theme: Theme) => ({
  historyItem: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 0),
    width: '100%',

    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  historyItemTopSection: {
    alignItems: 'flex-start',
    display: 'flex',
    flexFlow: 'row nowrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  itemName: {
    paddingTop: theme.spacing(0.75),
  },
  orderedItemsList: {
    '& ul': {
      padding: 0,
    },
  },
  snackbar: {
    marginTop: theme.spacing(1.75),
  },
  yourOrder: {
    '& .fd-text': {
      paddingBottom: theme.spacing(1.5),
    },
  },
}));

type HistoryItemsListProps = {
  currency: Currency;
  languageCode: string;
  orderItems: OrderHistoryItems[];
  translate: TranslateFunction;
  unavailableOrderItemIds?: number[];
};

export function HistoryItemsList(props: HistoryItemsListProps): JSX.Element {
  const {
    currency,
    languageCode,
    orderItems,
    translate,
    unavailableOrderItemIds = [],
  } = props;

  const classes = useStyles();

  const orderItemsNoDuplications = useMemo(() => {
    const orderItemsArray = [] as OrderHistoryItems[];

    orderItems.forEach((element) => {
      const isDuplicated = orderItemsArray.find(
        (item) =>
          _isEqual(item.Name, element.Name) &&
          _isEqual(
            item.OrderHistoryItemOptions,
            element.OrderHistoryItemOptions,
          ),
      );

      if (isDuplicated) {
        const existingItem = orderItemsArray.find(
          (item) => item.Name === element.Name,
        );
        existingItem.Quantity += 1;
        return null;
      }
      Object.assign(element, { Quantity: 1 });

      return orderItemsArray.push(element);
    });

    return orderItemsArray;
  }, [orderItems]);

  const nameAndQuantity = (item): string =>
    item.Quantity === 1 ? `${item.Name}` : `${item.Quantity}x ${item.Name}`;

  const renderItemOptions = (itemOption): JSX.Element =>
    itemOption.map((option) => (
      <Typography
        key={option.MenuItemOptionId.toString()}
        variant="body2-secondary"
      >
        {option.Name}
      </Typography>
    ));

  const localisedTotal = (item): string =>
    formatNumberToCurrency(
      item.PriceIncludingOptionSetItems * item.Quantity,
      currency,
      languageCode,
    );

  const renderItemsList = (): JSX.Element[] =>
    orderItemsNoDuplications.map((item) => (
      <li className={classes.historyItem} key={item.OrderItemId.toString()}>
        <div className={classes.historyItemTopSection}>
          <div>
            <Typography className={classes.itemName} variant="h3">
              {nameAndQuantity(item)}
            </Typography>

            {!!item.OrderHistoryItemOptions.length &&
              renderItemOptions(item.OrderHistoryItemOptions)}
          </div>
          <PriceTag
            horizontalAlign="right"
            localisedPrice={localisedTotal(item)}
            translate={translate}
          />
        </div>

        {unavailableOrderItemIds.includes(item.MenuItemId) && (
          <Snackbar
            className={classes.snackbar}
            message={translate('Reorder_Unavailable') as string}
            size="xs"
            type="secondary"
            variant="warning"
          />
        )}
      </li>
    ));

  return (
    <>
      <li className={classes.yourOrder}>
        <Typography variant="body2-secondary">
          {translate('Your_order')}
        </Typography>
      </li>

      <li className={classes.orderedItemsList}>
        <ul>{renderItemsList()}</ul>
      </li>
    </>
  );
}
